/* eslint-disable @nx/enforce-module-boundaries */
import { Injectable, inject } from '@angular/core';

import { CompanyModel } from '@ninety/mobile/platform/api';
import { environment } from '@ninety/mobile/platform/environments';
import { DatadogLogger, SegmentContactTraits } from '@ninety/mobile/platform/services';

import {
  IGainsightGlobalContext,
  IGainsightPx,
  IPXAccount,
  IPXConfiguration,
  IPXIdentifyUser,
  IPXUser,
} from '../models/gainsight-global-context';

@Injectable({ providedIn: 'root' })
export class GainsightPXService {
  /**
   * Static SDK config — built once, never per-call.
   *
   * `shouldTrackPages` and `shouldTrackTapEvents` are handled by the injected JS shim itself; the
   * remaining keys are forwarded to the native SDK untouched, so a key it does not honor fails
   * silently. `trackApplicationLifecycleEvents` is set explicitly because Gainsight documents
   * Android as auto-collecting app events while iOS requires them to be configured — and with the
   * Segment→PX cloud destination off, this is the only source of app open/background in PX.
   */
  private static readonly PX_CONFIG: IPXConfiguration = {
    apiKey: environment.GAINSIGHT_PX_API_KEY,
    // Undocumented, and absent from Gainsight's own config type. `handleStateChange` in the injected
    // shim guards on `configuration.enabled`, which the shim never assigns anywhere — so without this
    // the page tracker's `history.pushState` wrapper returns before calling through, silently
    // swallowing every navigation and stranding Ionic's tab stack on the page it booted with.
    enabled: true,
    shouldTrackPages: true,
    shouldTrackTapEvents: true,
    reportTrackingIssues: true,
    trackApplicationLifecycleEvents: true,
  };

  private logger = inject(DatadogLogger);

  /** Tracks whether the SDK has been initialized. Gainsight requires initialize() run once. */
  private initialized = false;

  /**
   * The bridge object the native SDK injects into the WebView.
   *
   * Read through a getter on EVERY call, never cached: the native `loadScript` call re-injects the
   * shim on every page load, replacing `window.gainsightpx` with a fresh object. A constructor
   * field or cached reference would go stale at the first navigation.
   */
  private get gainsightpx(): IGainsightPx {
    return (window as any)?.gainsightpx;
  }

  /**
   * Initialize the Gainsight PX SDK, returning whether it is ready to use. Idempotent — Gainsight
   * requires initialize() run only once.
   *
   * Every public method calls this first rather than relying on a single attempt at app start. The
   * native bridge injects `window.gainsightpx` at page-load-complete, which can land *after*
   * appReady; treating that first miss as terminal left PX dead for the whole session.
   */
  initialize(): boolean {
    if (this.initialized) return true;

    if (!this.gainsightpx) return false;

    try {
      this.gainsightpx.initialize(GainsightPXService.PX_CONFIG);
      this.initialized = true;
      this.logger.info({ message: '[GainsightPX] SDK initialized' });
    } catch (error) {
      this.logger.error({ message: '[GainsightPX] initialize() failed', error });
    }

    return this.initialized;
  }

  /** Identify the on-device session's user + account (required for engagement targeting). */
  identifyUser(ctx: IGainsightGlobalContext, user?: SegmentContactTraits, company?: CompanyModel): void {
    if (!this.initialize()) return;

    try {
      const pxIdentify = {
        user: {
          accountId: user?.company?.id || company?._id,
          id: ctx.currentPersonId,
          email: user?.email,
          gender: user?.gender,
          firstName: user?.firstName,
          lastName: user?.lastName,
          title: user?.title,
          phone: user?.phone,
          countryCode: company?.countryCode,
          countryName: user?.address?.country,
          stateName: user?.address?.state,
          city: user?.address?.city,
          street: user?.address?.street,
          organization: `${user?.company?.id || company?._id || ''}` || undefined,
          organizationEmployees:
            `${user?.company?.employee_count || company?.employeeRange?.length || ''}` || undefined,
          organizationIndustry: user?.company?.industry || company?.industry?.toString() || '',
          customAttributes: { ...ctx, ...(user || {}) },
        } as IPXUser,
        account: {
          id: user?.company?.id || company?._id,
          name: user?.company?.name || company?.name,
          trackedSubscriptionId: company?.subscription?.id,
          industry: user?.company?.industry || company?.industry?.toString(),
          numberofEmployees: user?.company?.employee_count || company?.employeeRange?.length,
          website: user?.website,
          plan: user?.company?.plan || company?.subscription?.plan,
          countryCode: company?.countryCode,
          countryName: user?.address?.country,
          stateName: user?.address?.state,
          city: user?.address?.city,
          street: user?.address?.street,
          customAttributes: { ...ctx, ...(user?.company || {}), ...(company || {}) },
          // sicCode: undefined, // <-- NOT SURE ON THESE YET
          // stateCode: undefined,
          // naicsCode: undefined,
          // continent: undefined,
          // postalCode: undefined,
          // regionName: undefined,
          // timeZone: undefined,
          // latitude: undefined,
          // longitude: undefined,
          // sfdcId: undefined,
        } as IPXAccount,
      } as IPXIdentifyUser;

      this.gainsightpx.identifyUser(pxIdentify);
    } catch (error) {
      this.logger.error({ message: '[GainsightPX] identifyUser() failed', error });
    }
  }

  /** Set Gainsight PX global context — session-level metadata auto-attached to all events. */
  setGlobalContext(ctx: IGainsightGlobalContext): void {
    if (!this.initialize()) return;

    try {
      this.gainsightpx.setGlobalContext(ctx);
    } catch (error) {
      this.logger.error({ message: '[GainsightPX] setGlobalContext() failed', error });
    }
  }

  /**
   * Clear the user identity/session on logout. Per Gainsight's hybrid-SDK docs, reset()
   * clears identity WITHOUT de-initializing — the next login re-identifies (identify$ on
   * appAndCompanyReady), it does not re-initialize. So we do NOT touch `initialized`.
   * (The reset-before-init force-unwrap crash was fixed in PXKit 1.13.2; we ship 1.13.4.)
   */
  resetSession(): void {
    // Deliberately does NOT initialize — there is no session to clear on an uninitialized SDK. The
    // bridge can also be momentarily absent between loadScript re-injections, which is a no-op too.
    if (!this.initialized || !this.gainsightpx) return;

    try {
      this.gainsightpx.reset();
    } catch (error) {
      this.logger.error({ message: '[GainsightPX] reset() failed', error });
    }
  }

  /**
   * Log a custom event with Gainsight PX.
   *
   * In-app engagements are evaluated by the on-device SDK against the events it sees locally, so an
   * event only reaches engagement targeting if it is tracked here. Events forwarded to PX from the
   * Segment cloud destination land in PX analytics but never reach this device.
   */
  customEvent(eventName: string, properties: Record<string, unknown> = {}): void {
    if (!this.initialize()) return;

    try {
      this.gainsightpx.customEvent({ eventName, properties: GainsightPXService.toPxProperties(properties) });
    } catch (error) {
      this.logger.error({ message: '[GainsightPX] customEvent() failed', error, data: { eventName } });
    }
  }

  /** Enter the PX engagement-editor mode from its deep link (scheme `gainsight.px.<subscriptionKey>`). */
  enterEditingMode(url: string): void {
    if (!this.initialize()) return;

    try {
      this.gainsightpx.enterEditingMode(url);
    } catch (error) {
      this.logger.error({ message: '[GainsightPX] enterEditingMode() failed', error });
    }
  }

  /**
   * PX custom-event properties are scalar-only (`Record<string, string>` on the wire). Coerce
   * whatever the call site passes, dropping `null` and `undefined` so engagement rules never match
   * on the string "undefined". Empty strings are preserved.
   */
  private static toPxProperties(properties: Record<string, unknown>): Record<string, string> {
    return Object.entries(properties).reduce<Record<string, string>>((acc, [key, value]) => {
      if (value === null || value === undefined) return acc;
      acc[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
      return acc;
    }, {});
  }
}
